import { IconArrowLeft, IconClock, IconTag } from "@tabler/icons-react";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { allPosts } from "content-collections";
import { z } from "zod";
import CustomMDX from "@/components/MDX";
import { ArticlePageGrid, ArticleTocPanel } from "@/components/mdx/article-toc";
import { BlogHeader } from "@/components/misc/BlogHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import ShareLinks from "@/components/share-links";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { config } from "@/data/config";
import { isModuleEnabled } from "@/lib/modules";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/_marketing/blog/$path")({
	component: RouteComponent,
	beforeLoad: ({ params }) => {
		if (!isModuleEnabled("marketing_blog")) {
			throw redirect({ to: "/" });
		}
		const parsed = z.string().safeParse(params.path);
		const post = parsed.success
			? allPosts.find(
					(post) => post._meta.path === parsed.data && post.published,
				)
			: undefined;
		if (!post) {
			// Unknown slugs are a 404, not a redirect to the index — redirects
			// teach crawlers that junk URLs resolve and pollute analytics.
			throw notFound();
		}
		return { post };
	},
	head: ({ params: { path } }) => {
		const post = allPosts.find((post) => post._meta.path === path);
		const dateIso =
			post?.date != null
				? new Date(post.date as string | Date).toISOString()
				: undefined;
		return {
			...seo({
				title: `${post?.title} - Blog`,
				description: post?.summary,
				keywords: post?.category,
				type: "Article",
				datePublished: dateIso,
				dateModified: dateIso,
				image: post?.image,
			}),
		};
	},
});

export function RouteComponent() {
	const { post } = Route.useRouteContext();
	const url = `${config.siteUrl}/blog/${post?._meta.path}`;
	const tocItems = post?.tableOfContents ?? [];
	const hasToc = tocItems.length > 0;

	return (
		<MarketingShell>
			<article className="pb-20 pt-12 sm:pb-24 sm:pt-16">
				<MarketingContainer>
					<Button
						variant="ghost"
						size="sm"
						className="-ms-2 mb-8 rounded-full"
						onClick={() => window.location.assign("/blog")}
					>
						<IconArrowLeft className="size-4" aria-hidden />
						Back to blog
					</Button>

					<BlogHeader
						heading={post?.title ?? ""}
						description={`by ${post?.author ?? "Francis Baker"}`}
						image={post?.image}
						imageAlt={post?.imageAlt ?? post?.title}
						credit={post?.imageCredit}
					/>

					<div className="mb-10 flex flex-wrap gap-3">
						{post?.date ? (
							<span className="inline-flex items-center gap-2 rounded-full border border-border/80 bg-muted/25 px-3 py-1.5 text-sm text-muted-foreground">
								<IconClock className="size-3.5 text-primary" aria-hidden />
								{new Date(post.date).toLocaleDateString("en-US", {
									year: "numeric",
									month: "long",
									day: "numeric",
								})}
							</span>
						) : null}
						{post?.category ? (
							<span className="inline-flex items-center gap-2 rounded-full border border-border/80 bg-muted/25 px-3 py-1.5 text-sm">
								<IconTag className="size-3.5 text-primary" aria-hidden />
								<Badge
									variant="secondary"
									className="border-0 bg-transparent px-0 font-normal"
								>
									{post.category}
								</Badge>
							</span>
						) : null}
						{post?.readingTime ? (
							<span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary">
								{post.readingTime}
							</span>
						) : null}
					</div>

					<ArticlePageGrid
						sidebar={
							hasToc ? (
								<ArticleTocPanel items={tocItems} variant="sidebar" />
							) : null
						}
					>
						{hasToc ? (
							<ArticleTocPanel items={tocItems} variant="inline" />
						) : null}

						<div className="mb-12 rounded-2xl border border-border/80 bg-card/60 p-6 ring-1 ring-border/40 sm:p-8 lg:p-10">
							<div className="prose prose-lg max-w-none dark:prose-invert prose-headings:scroll-mt-24 prose-a:text-primary">
								<CustomMDX mdxModule={post?.mdxModule} />
							</div>
						</div>

						<div className="rounded-xl border border-border/80 bg-card/50 p-6 ring-1 ring-border/40">
							<ShareLinks text={post?.title || ""} url={url} />
						</div>
					</ArticlePageGrid>

					<div className="mt-16 rounded-2xl border border-border/80 bg-muted/15 p-8 text-center dark:bg-muted/10 sm:p-10">
						<h2 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
							Enjoyed this article?
						</h2>
						<p className="mx-auto mt-2 max-w-md text-pretty text-muted-foreground">
							More notes on building products, infrastructure, and teams.
						</p>
						<Button
							className="mt-6 rounded-full px-6"
							size="lg"
							onClick={() => {
								window.location.href = "/blog";
							}}
						>
							Browse the blog
							<IconArrowLeft className="size-4 rotate-180" aria-hidden />
						</Button>
					</div>
				</MarketingContainer>
			</article>
		</MarketingShell>
	);
}
